import { useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Bot,
  Building2,
  Calculator,
  CarFront,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  ExternalLink,
  HeartHandshake,
  Home,
  Info,
  Landmark,
  Leaf,
  MapPin,
  Menu,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  UtensilsCrossed,
  X,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type Site = {
  rank: number;
  name: string;
  price: number;
  acres: number;
  status: string;
  badge: string;
  upside: string;
  caution: string;
  link: string;
};

const sites: Site[] = [
  {
    rank: 1,
    name: "3309 San Rey Lane",
    price: 6.75,
    acres: 27,
    status: "Active listing · Bonsall/Fallbrook",
    badge: "Best conditional fit",
    upside:
      "Broker materials describe prior County scoping, fire coordination and phased residential engineering near I-15.",
    caution:
      "Parcel records conflict on acreage, APN, open-space use and flood zone. Reconcile the full assemblage before any non-refundable deposit.",
    link: "https://www.landsearch.com/properties/3309-san-rey-ln-bonsall-ca-92003/4339525",
  },
  {
    rank: 2,
    name: "3264 Shearer Crossing",
    price: 4.2,
    acres: 93.36,
    status: "Active listing · Fallbrook",
    badge: "Backup / land bank",
    upside:
      "About 20 acres are described as flat and usable, with paved access, power, wells and water rights.",
    caution:
      "A70/A72 rural zoning and utility claims require a written County determination and sewer will-serve letter.",
    link: "https://www.zillow.com/homedetails/3264-Shearer-Xing-Fallbrook-CA-92028/16572556_zpid/",
  },
  {
    rank: 3,
    name: "40390 Ross Road",
    price: 2.628,
    acres: 80.05,
    status: "Active listing · De Luz",
    badge: "Low basis, high burden",
    upside:
      "Five legal lots, public-water meters, a well, power and a 153.6 kW solar array.",
    caution:
      "Extreme wildfire exposure, rolling terrain, septic dependence and a longer trip to hospital and staff base.",
    link: "https://www.redfin.com/CA/Fallbrook/40390-Ross-Rd-92028/home/57216260",
  },
  {
    rank: 4,
    name: "2125 Puerto del Mundo",
    price: 1.079,
    acres: 22.68,
    status: "Active listing · Fallbrook",
    badge: "Option only",
    upside:
      "Lowest entry price, productive grove, graded pads and several access points.",
    caution:
      "Steep-hillside and fire overlays plus uncertain utilities make the apparent land saving fragile.",
    link: "https://www.loopnet.com/Listing/2125-Puerto-del-Mundo-Fallbrook-CA/36924710/",
  },
];

const sources = [
  [
    "Pew: Indians in the U.S.",
    "https://www.pewresearch.org/race-and-ethnicity/fact-sheet/asian-americans-indians-in-the-u-s/",
  ],
  [
    "County demographic profile",
    "https://www.sandiegocounty.gov/content/dam/sdc/hhsa/programs/phs/CHS/2023%20City%20Demographic%20Profiles%20FINAL_02.05.25.pdf",
  ],
  [
    "California care-cost survey",
    "https://investor.genworth.com/news-events/press-releases/detail/996/long-term-care-costs-increase-in-california-exceeding",
  ],
  [
    "Vi La Jolla 2026 fees",
    "https://www.viliving.com/-/media/ViLiving/Home/locations/ca/san-diego-la-jolla/floor-plans-pricing/pricing/LJV-schedule-of-fees-2026-.pdf",
  ],
  [
    "South Asian seniors and loneliness",
    "https://pubmed.ncbi.nlm.nih.gov/33817827/",
  ],
  [
    "Transportation and quality of life",
    "https://pubmed.ncbi.nlm.nih.gov/27245988/",
  ],
  [
    "Food preferences of older immigrants",
    "https://pmc.ncbi.nlm.nih.gov/articles/PMC11289087/",
  ],
  [
    "CBRE 2026 development costs",
    "https://www.cbre.com/insights/briefs/2026-senior-housing-development-costs",
  ],
  [
    "County permit estimates",
    "https://www.sandiegocounty.gov/content/dam/sdc/dplu/docs/cost-schedule-summary.pdf",
  ],
  [
    "CDSS RCFE application",
    "https://www.cdss.ca.gov/inforesources/community-care/ascp-centralized-application-units",
  ],
  [
    "California CCRC rules",
    "https://cdss.ca.gov/inforesources/community-care/continuing-care/laws-and-regulations",
  ],
  [
    "Sonida 2026 operating benchmark",
    "https://www.sec.gov/Archives/edgar/data/1043000/000104300026000016/sndaq12026presentation_m.htm",
  ],
  [
    "San Diego occupational wages",
    "https://www.bls.gov/regions/west/news-release/2025/occupationalemploymentandwages_sandiego_20250501.htm",
  ],
  [
    "HUD residential care finance",
    "https://www.hud.gov/hud-partners/healthcare-programs",
  ],
  [
    "EasyMile safety report",
    "https://easymile.com/sites/default/files/easymile_safety_report_2023_2.pdf",
  ],
  [
    "ADA transportation rules",
    "https://www.ada.gov/law-and-regs/regulations/title-iii-regulations/",
  ],
  [
    "HIPAA business associates",
    "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/business-associates/index.html",
  ],
  [
    "California fair-housing guidance",
    "https://calcivilrights.ca.gov/Housing/",
  ],
];

const money = (value: number, decimals = 0) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: decimals,
  }).format(value);

const compactMoney = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

function RangeField({
  label,
  value,
  min,
  max,
  step,
  onChange,
  display,
  help,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (next: number) => void;
  display: string;
  help?: string;
}) {
  return (
    <label className="range-field">
      <span className="range-label">
        <span>{label}</span>
        <strong>{display}</strong>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {help && <small>{help}</small>}
    </label>
  );
}

function SourceLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a className="source-link" href={href} target="_blank" rel="noreferrer">
      {children} <ExternalLink size={12} aria-hidden="true" />
    </a>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scenario, setScenario] = useState<"base" | "downside">("base");
  const defaults = {
    units: 180,
    occupancy: 90,
    baseFee: 8500,
    otherRevenue: 950,
    projectCost: 85,
    debtPercent: 59,
    interestRate: 6.5,
    loanTerm: 40,
    variableCost: 50,
    fixedCost: 3.5,
  };
  const downsideDefaults = {
    units: 180,
    occupancy: 80,
    baseFee: 8200,
    otherRevenue: 800,
    projectCost: 100,
    debtPercent: 55,
    interestRate: 7.5,
    loanTerm: 35,
    variableCost: 52,
    fixedCost: 3.85,
  };
  const [inputs, setInputs] = useState(defaults);

  const update = (key: keyof typeof defaults, value: number) =>
    setInputs((current) => ({ ...current, [key]: value }));

  const loadScenario = (next: "base" | "downside") => {
    setScenario(next);
    setInputs(next === "base" ? defaults : downsideDefaults);
  };

  const model = useMemo(() => {
    const monthlyOccupiedRevenue = inputs.baseFee + inputs.otherRevenue;
    const annualRevenue =
      inputs.units * (inputs.occupancy / 100) * 12 * monthlyOccupiedRevenue;
    const noi =
      annualRevenue * (1 - inputs.variableCost / 100) -
      inputs.fixedCost * 1_000_000;
    const loan = inputs.projectCost * 1_000_000 * (inputs.debtPercent / 100);
    const monthlyRate = inputs.interestRate / 100 / 12;
    const payments = inputs.loanTerm * 12;
    const monthlyDebt =
      loan * (monthlyRate / (1 - Math.pow(1 + monthlyRate, -payments)));
    const annualDebt = monthlyDebt * 12;
    const dscr = annualDebt > 0 ? noi / annualDebt : 0;
    const equity = inputs.projectCost * 1_000_000 - loan;
    const cashFlow = noi - annualDebt;
    const cashYield = equity > 0 ? cashFlow / equity : 0;
    const operatingBreakEven =
      (inputs.fixedCost * 1_000_000) /
      (inputs.units *
        12 *
        monthlyOccupiedRevenue *
        (1 - inputs.variableCost / 100));
    const cashBreakEven =
      (inputs.fixedCost * 1_000_000 + annualDebt) /
      (inputs.units *
        12 *
        monthlyOccupiedRevenue *
        (1 - inputs.variableCost / 100));
    return {
      annualRevenue,
      noi,
      loan,
      annualDebt,
      dscr,
      equity,
      cashFlow,
      cashYield,
      operatingBreakEven,
      cashBreakEven,
    };
  }, [inputs]);

  const dscrTone =
    model.dscr >= 1.5 ? "green" : model.dscr >= 1.3 ? "amber" : "red";
  const feasibilityText =
    dscrTone === "green"
      ? "This case clears the planning covenant with breathing room. Move to lender screening and contractor pricing."
      : dscrTone === "amber"
        ? "This case is close, but vulnerable. Reduce debt, lower cost, raise occupied-unit revenue, or add mission capital."
        : "This case does not yet support the proposed debt. Treat that as a design prompt: phase smaller, densify, lower cost or bring more patient equity.";

  const tenYear = [
    { year: "Y1", base: -0.13, downside: -0.98 },
    { year: "Y2", base: 0.89, downside: -0.14 },
    { year: "Y3", base: 2.8, downside: 1.15 },
    { year: "Y4", base: 3.41, downside: 1.54 },
    { year: "Y5", base: 5.48, downside: 2.63 },
    { year: "Y6", base: 6.35, downside: 3.16 },
    { year: "Y7", base: 6.79, downside: 3.29 },
    { year: "Y8", base: 6.99, downside: 3.22 },
    { year: "Y9", base: 7.2, downside: 3.14 },
    { year: "Y10", base: 7.42, downside: 3.06 },
  ];

  const costData = [
    { name: "Buildings", value: 72, color: "#17324d" },
    { name: "Site", value: 20, color: "#2f6b55" },
    { name: "Soft costs", value: 16.6, color: "#e6a93b" },
    { name: "Finance/opening", value: 14, color: "#a9425b" },
    { name: "Land", value: 6.8, color: "#70867d" },
    { name: "FF&E / tech", value: 7, color: "#7b6c9d" },
    { name: "Contingency", value: 7.4, color: "#b3a889" },
  ];

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="Ananda Village home">
          <span className="brand-mark" aria-hidden="true">
            अ
          </span>
          <span>
            <strong>Ananda Village</strong>
            <small>A full-service retirement village</small>
          </span>
        </a>
        <button
          className="menu-button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
        <nav className={menuOpen ? "nav-links open" : "nav-links"}>
          {[
            ["The vision", "#vision"],
            ["Life at Ananda", "#community"],
            ["Sites", "#sites"],
            ["Plan", "#plan"],
            ["Economics", "#economics"],
            ["Technology", "#technology"],
            ["Next 100 days", "#next"],
            ["Developer ledger", "/developer"],
          ].map(([label, href]) => (
            <a key={href} href={href} onClick={() => setMenuOpen(false)}>
              {label}
            </a>
          ))}
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <img
            className="hero-image"
            src="/images/ananda-campus-concept.webp"
            alt="Conceptual view of a senior village in the North San Diego hills with courtyard homes, gardens, a small mandir and golf carts"
          />
          <div className="hero-shade" />
          <div className="hero-copy">
            <p className="eyebrow light">
              A new kind of retirement life in San Diego
            </p>
            <h1>
              Everything you love.
              <br />
              None of the work.
              <br />
              All of us together.
            </h1>
            <p className="hero-lede">
              You have spent a lifetime taking care of everyone else. Now wake
              up to your own beautiful home, hot chai, familiar food, temple,
              music, laughter, rides at your door and friends who are ready to
              enjoy the day with you. This is your time.
            </p>
            <div className="hero-actions">
              <a className="button primary" href="#community">
                Discover life at Ananda <HeartHandshake size={18} />
              </a>
              <a className="button ghost" href="#plan">
                Tour the village plan <ArrowRight size={18} />
              </a>
              <a className="button ghost" href="/developer">
                Developer ledger <TrendingUp size={18} />
              </a>
            </div>
          </div>
          <div className="hero-note">
            <span>Original concept image</span>
            <strong>Representative — not a site or permit drawing</strong>
          </div>
        </section>

        <section className="thesis-strip" aria-label="Feasibility summary">
          <div>
            <span>Two ways to live here</span>
            <strong>Rent or own</strong>
            <small>one remarkable community life</small>
          </div>
          <div>
            <span>Recommended scale</span>
            <strong>180</strong>
            <small>homes in three operable phases</small>
          </div>
          <div>
            <span>National source pool</span>
            <strong>5.2M</strong>
            <small>people identifying as Indian in 2023</small>
          </div>
          <div>
            <span>The honest gate</span>
            <strong>≥ 1.50×</strong>
            <small>stabilized debt coverage</small>
          </div>
        </section>

        <section id="vision" className="section section-intro">
          <div className="section-kicker">
            <Sparkles size={18} /> The simple version
          </div>
          <div className="intro-grid">
            <div>
              <h2>
                Retirement is not the end of the fun. It is when the fun finally
                has time for you.
              </h2>
            </div>
            <div className="intro-copy">
              <p className="lead">
                Imagine beginning the morning with a garden walk, meeting
                friends over chai, enjoying food made the way you like it,
                visiting the puja room, joining cards, music, a film or an
                outing—and never wondering who will drive or who will help.
              </p>
              <p>
                Ananda is designed to remove the chores and distances that make
                daily life harder while preserving privacy, choice and
                independence. For investors, that emotional promise is paired
                with discipline: founder deposits first, land under option,
                financeable scale and an 80-home opening phase that already
                feels complete.
              </p>
            </div>
          </div>
          <div className="verdict-grid">
            <article className="verdict-card positive">
              <BadgeCheck />
              <h3>The promise residents feel</h3>
              <ul>
                <li>
                  A private home without cooking, cleaning or maintenance
                  burdens
                </li>
                <li>
                  Familiar food, spiritual life and everyday companionship
                </li>
                <li>Rides, wellness and practical help within the community</li>
                <li>Choice and independence—with reassurance close at hand</li>
              </ul>
            </article>
            <article className="verdict-card caution">
              <ShieldCheck />
              <h3>The discipline investors need</h3>
              <ul>
                <li>Option—not purchase—before entitlement proof</li>
                <li>
                  Rental RCFE first; avoid launch-stage lifetime-care promises
                </li>
                <li>Cost, occupancy and DSCR gates before every phase</li>
                <li>
                  Human care and mobility fallback behind every automation
                </li>
              </ul>
            </article>
          </div>
        </section>

        <section id="community" className="section community-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Life at Ananda</p>
              <h2>Everything that makes a day good, close to home.</h2>
            </div>
            <p>
              Home should make life easier. Community should make it joyful.
              Every resident’s language, faith, food, privacy, family
              relationships and care preferences remain personal—not assumptions
              made for them.
            </p>
          </div>
          <div className="editorial-image-grid">
            <figure className="feature-image dining-image">
              <img
                src="/images/ananda-dining-concept.webp"
                alt="Conceptual shared dining room with Indian seniors enjoying lunch together"
              />
              <figcaption>
                Daily dining is social infrastructure, not a meal transaction.
              </figcaption>
            </figure>
            <div className="program-list">
              {[
                [
                  Home,
                  "A home that asks less of you",
                  "A private, accessible apartment kept beautifully maintained—without repairs, housekeeping chores or the isolation of living alone.",
                ],
                [
                  UtensilsCrossed,
                  "Food that feels like home",
                  "Three meals and snacks; vegetarian-first; regional rotations; Jain, halal, fasting and clinical options; room delivery when desired.",
                ],
                [
                  HeartHandshake,
                  "Support that protects independence",
                  "A person answers 24/7. Rides and wellness checks are close by. Licensed medication and personal-care support can be added through a separately assessed care tier.",
                ],
                [
                  Landmark,
                  "Faith, friendship and fun",
                  "Puja and quiet reflection, chai with friends, films, music, games, festivals and family visits give every day somewhere to go and someone to enjoy it with.",
                ],
              ].map(([Icon, title, copy]) => (
                <article key={String(title)}>
                  <Icon size={22} />
                  <div>
                    <h3>{title as string}</h3>
                    <p>{copy as string}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <div className="evidence-band">
            <div>
              <strong>29.9%</strong>
              <span>
                of a 682-person South Asian 60+ sample sometimes felt lonely
              </span>
            </div>
            <div>
              <strong>39.1%</strong>
              <span>reported some emotional distress</span>
            </div>
            <div>
              <strong>84%</strong>
              <span>
                of Indians age 5+ are English-proficient—still not a reason to
                ignore language preference
              </span>
            </div>
            <SourceLink href="https://pubmed.ncbi.nlm.nih.gov/33817827/">
              Read the loneliness study
            </SourceLink>
          </div>
        </section>

        <section id="sites" className="section sites-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Where it could go</p>
              <h2>Four listings. One conditional front-runner.</h2>
            </div>
            <p>
              Listings are a starting point, not a zoning opinion. Price and
              status were researched July 18, 2026 and can change without
              notice.
            </p>
          </div>
          <div className="site-layout">
            <div
              className="site-map"
              aria-label="Conceptual North County location map"
            >
              <div className="map-terrain terrain-one" />
              <div className="map-terrain terrain-two" />
              <div className="route-line" />
              <span className="map-label label-fallbrook">Fallbrook</span>
              <span className="map-label label-bonsall">Bonsall</span>
              <span className="map-label label-palomar">Palomar Medical</span>
              <span className="map-label label-san">SAN</span>
              {sites.map((site, index) => (
                <span key={site.name} className={`map-pin pin-${index + 1}`}>
                  <b>{site.rank}</b>
                </span>
              ))}
              <div className="map-legend">
                Conceptual orientation · not to scale
              </div>
            </div>
            <div className="site-list">
              {sites.map((site) => (
                <article className="site-card" key={site.name}>
                  <div className="site-rank">{site.rank}</div>
                  <div className="site-body">
                    <div className="site-title-row">
                      <div>
                        <h3>{site.name}</h3>
                        <span>{site.status}</span>
                      </div>
                      <strong>{money(site.price * 1_000_000)}</strong>
                    </div>
                    <div className="site-meta">
                      <span>{site.acres} acres</span>
                      <span>{site.badge}</span>
                    </div>
                    <p>
                      <Check size={15} /> {site.upside}
                    </p>
                    <p className="site-caution">
                      <Info size={15} /> {site.caution}
                    </p>
                    <SourceLink href={site.link}>
                      Open listing source
                    </SourceLink>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <div className="fatal-flaw-row">
            {[
              [
                "Written land-use path",
                "County confirms the exact IL + RCFE + spiritual/community mix.",
              ],
              [
                "Two safe ways out",
                "Fire engineer validates evacuation, fire flow and emergency access.",
              ],
              [
                "Utility certainty",
                "Water and sewer will-serve letters replace broker language.",
              ],
              [
                "150+ home yield",
                "Concept plan proves enough density after biology, slopes and open space.",
              ],
            ].map(([title, copy]) => (
              <div key={title}>
                <ShieldCheck />
                <strong>{title}</strong>
                <span>{copy}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="plan" className="section plan-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">A village that grows gracefully</p>
              <h2>Build the heart once. Add neighborhoods only when earned.</h2>
            </div>
            <p>
              The first phase opens with dining, worship, wellness and gardens
              complete. Residents never move into a construction promise.
            </p>
          </div>
          <div className="masterplan-wrap">
            <svg
              className="masterplan"
              viewBox="0 0 900 560"
              role="img"
              aria-labelledby="plan-title plan-desc"
            >
              <title id="plan-title">Conceptual phased master plan</title>
              <desc id="plan-desc">
                Courtyard residential clusters around a central commons with a
                mobility loop and gardens.
              </desc>
              <path
                className="site-boundary"
                d="M85 107 Q180 26 342 68 T631 56 Q805 76 843 213 T817 429 Q664 520 479 498 T153 475 Q53 352 85 107Z"
              />
              <path
                className="cart-loop"
                d="M170 171 Q313 87 486 115 T738 199 Q792 305 700 400 T449 444 Q271 455 172 360 T170 171Z"
              />
              <g className="phase phase-one">
                <rect x="336" y="213" width="224" height="137" rx="22" />
                <rect x="195" y="166" width="112" height="72" rx="18" />
                <rect x="187" y="266" width="125" height="78" rx="18" />
                <rect x="255" y="365" width="126" height="56" rx="18" />
              </g>
              <g className="phase phase-two">
                <rect x="592" y="150" width="128" height="72" rx="18" />
                <rect x="618" y="252" width="112" height="76" rx="18" />
                <rect x="547" y="370" width="132" height="60" rx="18" />
              </g>
              <g className="phase phase-three">
                <rect x="126" y="121" width="60" height="38" rx="13" />
                <rect x="103" y="211" width="60" height="38" rx="13" />
                <rect x="110" y="385" width="60" height="38" rx="13" />
                <rect x="701" y="105" width="60" height="38" rx="13" />
                <rect x="740" y="348" width="60" height="38" rx="13" />
              </g>
              <circle className="garden" cx="449" cy="278" r="45" />
              <text x="448" y="273" textAnchor="middle">
                COMMONS
              </text>
              <text x="448" y="291" textAnchor="middle">
                dining · wellness · puja
              </text>
              <text x="245" y="207" textAnchor="middle">
                PHASE 1
              </text>
              <text x="666" y="195" textAnchor="middle">
                PHASE 2
              </text>
              <text x="452" y="520" textAnchor="middle">
                Protected 8–10 mph mobility loop · all homes within 1,200 ft of
                the commons
              </text>
            </svg>
            <div className="phase-key">
              <article>
                <span className="phase-dot one" />
                <div>
                  <strong>Phase 1 · 80 homes</strong>
                  <p>
                    60 independent/supportive, 16 assisted, 4 respite/flex + the
                    complete shared heart.
                  </p>
                  <small>
                    Open only after ≥ 50 founder deposits and full financing.
                  </small>
                </div>
              </article>
              <article>
                <span className="phase-dot two" />
                <div>
                  <strong>Phase 2 · +40 homes</strong>
                  <p>
                    Add a residential cluster and activate reserved
                    dining/activity shell.
                  </p>
                  <small>
                    Start at 75% Phase 1 occupancy with staff and debt tests
                    met.
                  </small>
                </div>
              </article>
              <article>
                <span className="phase-dot three" />
                <div>
                  <strong>Phase 3 · +60 homes</strong>
                  <p>
                    Reach the 180-home scale needed to absorb 24/7 service and
                    the shared campus.
                  </p>
                  <small>Start at 85% occupancy across 120 open homes.</small>
                </div>
              </article>
            </div>
          </div>
          <div className="apartment-row">
            <figure className="feature-image apartment-image">
              <img
                src="/images/ananda-apartment-concept.webp"
                alt="Conceptual accessible one-bedroom apartment with wide clearances and a small puja niche"
              />
              <figcaption>
                Concept: a real home with aging-in-place details—not a hotel
                room.
              </figcaption>
            </figure>
            <div className="floorplan-card">
              <div className="card-header">
                <span>Representative 1-bedroom</span>
                <strong>820 net sq. ft.</strong>
              </div>
              <svg
                viewBox="0 0 530 360"
                role="img"
                aria-label="Conceptual one-bedroom floor plan"
              >
                <rect
                  className="fp-shell"
                  x="15"
                  y="15"
                  width="500"
                  height="330"
                />
                <path
                  className="fp-wall"
                  d="M15 192H210V15M210 127H345V15M345 215H515M345 127V345M210 280H345"
                />
                <rect
                  className="fp-balcony"
                  x="355"
                  y="226"
                  width="150"
                  height="106"
                />
                <rect
                  className="fp-kitchen"
                  x="225"
                  y="35"
                  width="105"
                  height="72"
                  rx="4"
                />
                <circle className="fp-turn" cx="117" cy="100" r="35" />
                <circle className="fp-turn" cx="280" cy="215" r="35" />
                <text x="110" y="65">
                  BEDROOM
                </text>
                <text x="275" y="70">
                  KITCHEN
                </text>
                <text x="95" y="245">
                  BATH
                </text>
                <text x="270" y="172">
                  DINING
                </text>
                <text x="423" y="160">
                  LIVING
                </text>
                <text x="425" y="285">
                  BALCONY
                </text>
                <text x="282" y="320">
                  PUJA / FLEX
                </text>
              </svg>
              <div className="floorplan-notes">
                <span>Zero-step</span>
                <span>36″ doors</span>
                <span>Curbless shower</span>
                <span>Induction</span>
                <span>Caregiver-flex</span>
              </div>
              <small>
                Concept only. Architect, accessibility consultant and fire
                authority must validate all dimensions.
              </small>
            </div>
          </div>
        </section>

        <section id="economics" className="section economics-section">
          <div className="section-heading light-heading">
            <div>
              <p className="eyebrow light">Make the numbers yours</p>
              <h2>A plain-English feasibility calculator.</h2>
            </div>
            <p>
              Move a few assumptions. The model explains what changes and never
              pretends a planning estimate is a lender quote.
            </p>
          </div>
          <div
            className="scenario-tabs"
            role="group"
            aria-label="Choose scenario"
          >
            <button
              className={scenario === "base" ? "active" : ""}
              onClick={() => loadScenario("base")}
            >
              Financeable target
            </button>
            <button
              className={scenario === "downside" ? "active" : ""}
              onClick={() => loadScenario("downside")}
            >
              Downside test
            </button>
            <button
              className="reset-button"
              onClick={() => loadScenario(scenario)}
            >
              <RotateCcw size={15} /> Reset
            </button>
          </div>
          <div className="calculator-shell">
            <div className="calculator-inputs">
              <h3>
                <Home /> The community
              </h3>
              <RangeField
                label="Homes at stabilization"
                value={inputs.units}
                min={80}
                max={240}
                step={10}
                onChange={(v) => update("units", v)}
                display={`${inputs.units}`}
                help="The service model becomes materially stronger near 180 homes."
              />
              <RangeField
                label="Occupied homes"
                value={inputs.occupancy}
                min={60}
                max={95}
                step={1}
                onChange={(v) => update("occupancy", v)}
                display={`${inputs.occupancy}%`}
              />
              <RangeField
                label="Base monthly fee"
                value={inputs.baseFee}
                min={6500}
                max={11000}
                step={100}
                onChange={(v) => update("baseFee", v)}
                display={money(inputs.baseFee)}
              />
              <RangeField
                label="Couples, care & extras"
                value={inputs.otherRevenue}
                min={0}
                max={2500}
                step={50}
                onChange={(v) => update("otherRevenue", v)}
                display={`${money(inputs.otherRevenue)}/occupied home`}
                help="A blended average—not a promise that care is unlimited."
              />

              <h3>
                <Building2 /> The build
              </h3>
              <RangeField
                label="All-in project cost"
                value={inputs.projectCost}
                min={70}
                max={180}
                step={1}
                onChange={(v) => update("projectCost", v)}
                display={`$${inputs.projectCost}M`}
                help="Includes land, buildings, site work, soft costs, contingency, finance and opening."
              />
              <RangeField
                label="Debt share"
                value={inputs.debtPercent}
                min={30}
                max={70}
                step={1}
                onChange={(v) => update("debtPercent", v)}
                display={`${inputs.debtPercent}%`}
              />
              <RangeField
                label="Interest rate"
                value={inputs.interestRate}
                min={4.5}
                max={10}
                step={0.1}
                onChange={(v) => update("interestRate", v)}
                display={`${inputs.interestRate.toFixed(1)}%`}
              />
              <RangeField
                label="Loan term"
                value={inputs.loanTerm}
                min={20}
                max={40}
                step={5}
                onChange={(v) => update("loanTerm", v)}
                display={`${inputs.loanTerm} years`}
              />

              <h3>
                <UtensilsCrossed /> Operations
              </h3>
              <RangeField
                label="Variable operating cost"
                value={inputs.variableCost}
                min={42}
                max={60}
                step={1}
                onChange={(v) => update("variableCost", v)}
                display={`${inputs.variableCost}% of revenue`}
              />
              <RangeField
                label="Fixed annual operations"
                value={inputs.fixedCost}
                min={2.5}
                max={6}
                step={0.05}
                onChange={(v) => update("fixedCost", v)}
                display={`$${inputs.fixedCost.toFixed(2)}M`}
              />
            </div>
            <div className="calculator-results">
              <div className={`result-verdict ${dscrTone}`}>
                <span>Planning result</span>
                <strong>{model.dscr.toFixed(2)}× DSCR</strong>
                <p>{feasibilityText}</p>
              </div>
              <div className="result-grid">
                <div>
                  <span>Annual resident revenue</span>
                  <strong>{compactMoney(model.annualRevenue)}</strong>
                </div>
                <div>
                  <span>Operating income (NOI)</span>
                  <strong>{compactMoney(model.noi)}</strong>
                </div>
                <div>
                  <span>Illustrative loan</span>
                  <strong>{compactMoney(model.loan)}</strong>
                </div>
                <div>
                  <span>Annual debt payments</span>
                  <strong>{compactMoney(model.annualDebt)}</strong>
                </div>
                <div>
                  <span>Equity / mission capital</span>
                  <strong>{compactMoney(model.equity)}</strong>
                </div>
                <div>
                  <span>Cash after debt</span>
                  <strong>{compactMoney(model.cashFlow)}</strong>
                </div>
              </div>
              <div className="break-even-card">
                <div>
                  <span>Operations cover themselves at</span>
                  <strong>
                    {Math.round(model.operatingBreakEven * 100)}% occupancy
                  </strong>
                </div>
                <div>
                  <span>Operations + this debt cover at</span>
                  <strong>
                    {Math.round(model.cashBreakEven * 100)}% occupancy
                  </strong>
                </div>
              </div>
              <div className="meter-wrap">
                <div className="meter-label">
                  <span>Debt coverage</span>
                  <span>Target: 1.50×</span>
                </div>
                <div className="meter">
                  <span
                    className={dscrTone}
                    style={{
                      width: `${Math.min(100, Math.max(3, (model.dscr / 2) * 100))}%`,
                    }}
                  />
                </div>
              </div>
              <p className="calculator-disclaimer">
                <Info size={15} /> Planning model only. Excludes taxes,
                depreciation, replacement reserves, fees and detailed care-tier
                mix. Actual debt is limited by lender LTV/LTC, appraisal, DSCR
                and program eligibility.
              </p>
            </div>
          </div>

          <div className="economics-grid">
            <article className="chart-card">
              <div className="card-header">
                <span>10-year operating ramp</span>
                <strong>NOI, $ millions</strong>
              </div>
              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={tenYear}
                    margin={{ top: 15, right: 5, left: -15, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="baseFill" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0%"
                          stopColor="#2f6b55"
                          stopOpacity={0.5}
                        />
                        <stop
                          offset="100%"
                          stopColor="#2f6b55"
                          stopOpacity={0.03}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#dfe6e2" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [`$${Number(value).toFixed(2)}M`]}
                    />
                    <Area
                      type="monotone"
                      dataKey="base"
                      stroke="#2f6b55"
                      fill="url(#baseFill)"
                      strokeWidth={3}
                      name="Base"
                    />
                    <Area
                      type="monotone"
                      dataKey="downside"
                      stroke="#a9425b"
                      fill="transparent"
                      strokeWidth={2}
                      strokeDasharray="6 5"
                      name="Downside"
                    />
                    <Legend />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p>
                Phase openings drive the visible steps in years 3 and 5. Sponsor
                equity and an interest/operating reserve bridge the ramp.
              </p>
            </article>
            <article className="chart-card">
              <div className="card-header">
                <span>154-home rural campus benchmark</span>
                <strong>$144M base</strong>
              </div>
              <div className="chart-wrap pie-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={costData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={62}
                      outerRadius={98}
                      paddingAngle={2}
                    >
                      {costData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${value}M`]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <p>
                This benchmark shows why cost control is the project’s defining
                gate. Base building allowance is $360/gross sq. ft.; rural site
                work adds $20M.
              </p>
            </article>
          </div>

          <div className="capital-options">
            <div className="section-heading compact">
              <div>
                <p className="eyebrow">Capital paths</p>
                <h2>Finance the service model—not just the real estate.</h2>
              </div>
              <p>
                Each path needs a specialist lender screen. None is a
                commitment.
              </p>
            </div>
            <div className="option-grid">
              {[
                [
                  "Bank + patient equity",
                  "Most flexible launch path",
                  "Construction loan at a planning 60–65% LTC plus family-office/mission equity and 18–24 months of interest and operating reserve.",
                ],
                [
                  "HUD 232",
                  "Best for the licensed-care component",
                  "Potential 40-year insured debt and 1.45× DSCR test, but independent-living share and licensing must fit the program.",
                ],
                [
                  "Nonprofit + CHFFA",
                  "Mission-aligned lower-cost capital",
                  "Tax-exempt or taxable revenue bonds can finance eligible nonprofit RCFE land, construction and working capital.",
                ],
                [
                  "USDA / C-PACE",
                  "Targeted supplements",
                  "Rural nonprofit facilities may qualify for USDA; energy, solar, battery and charging may fit C-PACE with senior-lender consent.",
                ],
              ].map(([title, kicker, copy]) => (
                <article key={title}>
                  <CircleDollarSign />
                  <small>{kicker}</small>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="technology" className="section technology-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Technology that disappears into service</p>
              <h2>Autonomy is a convenience. A human is always accountable.</h2>
            </div>
            <p>
              Launch with human-driven carts. Introduce driverless service only
              on a separated private loop after the community is operating and
              the safety case is signed.
            </p>
          </div>
          <div className="mobility-story">
            <div className="mobility-diagram">
              <div className="route-orbit orbit-one" />
              <div className="route-orbit orbit-two" />
              <div className="mobility-center">
                <CarFront />
                <strong>Ride desk</strong>
                <span>app · phone · kiosk · wearable</span>
              </div>
              <div className="mobility-node node-home">
                <Home />
                <span>Home</span>
              </div>
              <div className="mobility-node node-dining">
                <UtensilsCrossed />
                <span>Dining</span>
              </div>
              <div className="mobility-node node-care">
                <HeartHandshake />
                <span>Care</span>
              </div>
              <div className="mobility-node node-family">
                <Users />
                <span>Visit</span>
              </div>
              <div className="moving-cart">
                <CarFront />
              </div>
            </div>
            <div className="mobility-copy">
              <p className="quote">
                “I shouldn’t have to learn an app to get to dinner.”
              </p>
              <div className="fleet-numbers">
                <div>
                  <strong>6</strong>
                  <span>accessible AV shuttles at mature scale</span>
                </div>
                <div>
                  <strong>2</strong>
                  <span>wheelchair-accessible human-driven fallbacks</span>
                </div>
                <div>
                  <strong>10</strong>
                  <span>delivery robots, including spares</span>
                </div>
                <div>
                  <strong>≤ 10 min</strong>
                  <span>target 90th-percentile pickup</span>
                </div>
              </div>
              <div className="pilot-callout">
                <Bot />
                <div>
                  <strong>$0.65M–$1.0M first-year pilot allowance</strong>
                  <p>
                    Two accessible shuttles, mapping, depot, training, insurance
                    and contingency. Vendor quotes and insurer approval are
                    mandatory.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="reliability-grid">
            {[
              [
                ShieldCheck,
                "A small operating domain",
                "Private mapped loop, 8–10 mph, no public roads at launch, geofenced closures and marked crossings.",
              ],
              [
                Clock3,
                "Reliability that is measured",
                "≥99% scheduled availability, ≥98% mission completion, spare vehicle and N+1 chargers.",
              ],
              [
                CarFront,
                "A manual service in parallel",
                "Same hours and wait-time goal for wheelchair-accessible human service; urgent fallback dispatched within five minutes.",
              ],
              [
                Leaf,
                "Fail safe, not fail hidden",
                "Local safe stop on comms/localization fault, onboard and remote e-stop, voice link and quarterly emergency drills.",
              ],
            ].map(([Icon, title, copy]) => (
              <article key={String(title)}>
                <Icon />
                <h3>{title as string}</h3>
                <p>{copy as string}</p>
              </article>
            ))}
          </div>

          <div className="app-plan">
            <div
              className="phone-mockup"
              aria-label="Conceptual resident app home screen"
            >
              <div className="phone-bar">
                <span>9:41</span>
                <span>●●●</span>
              </div>
              <p className="phone-greeting">Namaste, Asha</p>
              <h3>What would make today easier?</h3>
              <button>
                <CarFront />
                <span>
                  <strong>Request a ride</strong>
                  <small>Typical pickup: 6 min</small>
                </span>
                <ChevronRight />
              </button>
              <button>
                <UtensilsCrossed />
                <span>
                  <strong>Today’s meals</strong>
                  <small>Gujarati lunch · order by 11</small>
                </span>
                <ChevronRight />
              </button>
              <button>
                <Users />
                <span>
                  <strong>What’s happening</strong>
                  <small>Chair yoga begins at 10</small>
                </span>
                <ChevronRight />
              </button>
              <button className="call-staff">
                <HeartHandshake />
                <span>
                  <strong>Call the front desk</strong>
                  <small>A person answers 24/7</small>
                </span>
              </button>
              <div className="phone-languages">
                English · हिन्दी · ગુજરાતી · తెలుగు · தமிழ்
              </div>
            </div>
            <div className="app-copy">
              <p className="eyebrow">One engineer, carefully scoped</p>
              <h2>Build the welcome layer. Buy the regulated systems.</h2>
              <p>
                A capable $150,000 engineer, AI assistance and focused
                contractors can ship a large-type resident PWA and staff console
                in six months. They should not build medical records, fall
                detection, payment custody or vehicle autonomy.
              </p>
              <div className="roadmap">
                <div>
                  <span>0–6 months</span>
                  <strong>Useful on day one</strong>
                  <p>
                    Meals, rides, events, housekeeping requests, announcements,
                    one-touch help and multilingual preferences.
                  </p>
                </div>
                <div>
                  <span>6–12 months</span>
                  <strong>Connect the family</strong>
                  <p>
                    Family permissions, visits, billing view and integrations
                    with fleet, kitchen and nurse-call vendors.
                  </p>
                </div>
                <div>
                  <span>12–24 months</span>
                  <strong>Personalize—with consent</strong>
                  <p>
                    Wait-time prediction and optional sensor alerts routed to
                    staff; no autonomous clinical decisions.
                  </p>
                </div>
              </div>
              <div className="tech-budget">
                <strong>$0.50M–$0.75M</strong>
                <span>
                  first-year product, integration, devices and independent
                  security/accessibility testing
                </span>
              </div>
            </div>
          </div>
        </section>

        <section id="next" className="section next-section">
          <div className="next-hero">
            <p className="eyebrow light">The next 100 days</p>
            <h2>Turn a beautiful hypothesis into evidence.</h2>
            <p>
              Do these in parallel before a land closing or a glossy presales
              promise.
            </p>
          </div>
          <div className="next-steps">
            {[
              [
                "Days 1–30",
                "Listen before designing",
                "Interview 40–60 seniors and adult children across regions, faiths, solo/couple status and care needs. Form a paid resident advisory circle.",
              ],
              [
                "Days 15–45",
                "Test the offer nationally",
                "Survey 300+ prospects; test fees from $6,500 to $11,000, unit size, rental versus deposit, San Diego locations and care tiers.",
              ],
              [
                "Days 20–60",
                "Option two sites",
                "Request title, entitlement files and utility evidence. Commission a fatal-flaw screen and secure a long due-diligence option—do not close.",
              ],
              [
                "Days 30–75",
                "Price one concept",
                "Give the same 30% concept to two senior-living contractors. Reconcile to the project cost gate and re-plan if it misses.",
              ],
              [
                "Days 45–90",
                "Screen operators and lenders",
                "Meet experienced RCFE operators, CDSS, insurance brokers, bank/HUD lenders and nonprofit capital partners.",
              ],
              [
                "Days 60–100",
                "Earn founder deposits",
                "Host a San Diego future-resident retreat and collect 25–50 refundable deposits with clear escrow and cancellation terms.",
              ],
            ].map(([time, title, copy], index) => (
              <article key={title}>
                <div className="step-number">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <small>{time}</small>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
          <div className="decision-gates">
            <h3>Greenlight Phase 1 only when all six are true</h3>
            <div>
              {[
                "50+ refundable founder deposits",
                "Written 180-home land-use path",
                "Two contractor budgets inside the gate",
                "Two safe access routes + utility letters",
                "RCFE operator and licensing plan",
                "Base DSCR ≥1.50×; downside has equity cure",
              ].map((gate) => (
                <span key={gate}>
                  <Check /> {gate}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="section technical-section">
          <details>
            <summary>
              <span>
                <Info /> Technical notes, assumptions and source library
              </span>
              <ChevronRight />
            </summary>
            <div className="technical-content">
              <div className="assumption-grid">
                <article>
                  <h3>What “full service” means here</h3>
                  <p>
                    Three meals + snacks; weekly housekeeping and linen; daily
                    common-area service; activities; scheduled/local transport;
                    24/7 response; supportive care priced in tiers. Skilled
                    nursing remains third-party or separately licensed.
                  </p>
                </article>
                <article>
                  <h3>What the fee does not promise</h3>
                  <p>
                    Unlimited personal care, 24-hour skilled nursing, unlimited
                    private aides, hospital services, unrestricted robot
                    delivery or guaranteed future pricing. These require
                    published tier schedules and contracts.
                  </p>
                </article>
                <article>
                  <h3>Construction benchmark</h3>
                  <p>
                    154 homes / about 200,000 gross sq. ft. / $144M base: $72M
                    building hard cost, $20M site, $16.6M soft, $14M
                    finance/opening, $7.4M contingency, $7M FF&E/tech and $6.8M
                    land. Range: $101M–$215M.
                  </p>
                </article>
                <article>
                  <h3>Operating benchmark</h3>
                  <p>
                    Stabilized target expense 70–73% of resident revenue,
                    informed by public senior-living operator data. At 80 homes,
                    fixed services may hold NOI to only 10–16% even after
                    occupancy stabilizes.
                  </p>
                </article>
                <article>
                  <h3>Licensing</h3>
                  <p>
                    Assisted living/memory care require California RCFE
                    licensing and fire clearance. A CCRC/life-plan contract adds
                    a CDSS Certificate of Authority. County entitlement and RCFE
                    permission are separate questions.
                  </p>
                </article>
                <article>
                  <h3>Fair housing and age qualification</h3>
                  <p>
                    The community is culturally fluent and open to every
                    qualified resident. Admissions and advertising cannot prefer
                    or exclude people because of national origin, ancestry,
                    race, religion, language or other protected status.
                    Specialized counsel must validate any 55+/62+ structure and
                    all marketing.
                  </p>
                </article>
              </div>
              <h3>Selected source library</h3>
              <div className="source-grid">
                {sources.map(([label, href]) => (
                  <SourceLink key={href} href={href}>
                    {label}
                  </SourceLink>
                ))}
              </div>
              <div className="legal-note">
                <ShieldCheck />
                <p>
                  <strong>
                    Feasibility concept—not investment, legal, medical,
                    architectural or zoning advice.
                  </strong>{" "}
                  Figures are in nominal 2026 U.S. dollars unless noted. Listing
                  availability, rates, program rules and regulations change. A
                  California land-use attorney, licensed architect, RCFE
                  operator, healthcare counsel, insurance broker, lender and tax
                  adviser must verify the project before reliance.
                </p>
              </div>
            </div>
          </details>
        </section>
      </main>

      <footer>
        <div className="footer-brand">
          <span className="brand-mark">अ</span>
          <div>
            <strong>Ananda Village</strong>
            <small>Live independently. Never live alone.</small>
          </div>
        </div>
        <div>
          <span>Research date</span>
          <strong>July 18, 2026</strong>
        </div>
        <div>
          <span>Prepared as</span>
          <strong>Investor feasibility concept</strong>
        </div>
        <a href="#top">Back to top ↑</a>
      </footer>
    </div>
  );
}

export default App;
